import { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { Loader2, Search, Download, ArrowLeft, Link2 } from 'lucide-react';

/**
 * Importa UM cliente de um painel externo para preencher o formulário de Novo Cliente.
 * Fontes vêm de GET /imports/sources (hoje: Sigma via login; pronto para outros painéis).
 * onImported({ name, phone, plan, expiration_date, import_data })
 */
const errText = (e, fb) => {
  const d = e?.response?.data?.detail;
  if (typeof d === 'string') return d;
  if (Array.isArray(d)) return d.map((x) => x?.msg || x?.message || '').filter(Boolean).join('; ') || fb;
  if (d && typeof d === 'object') return d.msg || d.message || fb;
  return e?.message || fb;
};

export default function ImportCustomerDialog({ open, onOpenChange, api, onImported }) {
  const [loadingSources, setLoadingSources] = useState(false);
  const [sources, setSources] = useState([]);
  const [sourceKey, setSourceKey] = useState('');
  const [query, setQuery] = useState('');
  const [searching, setSearching] = useState(false);
  const [results, setResults] = useState(null);
  const [chosen, setChosen] = useState(null);
  const [price, setPrice] = useState('');
  const [months, setMonths] = useState('1');
  const [resolving, setResolving] = useState(false);
  const [plans, setPlans] = useState([]);
  const [testPlanId, setTestPlanId] = useState('');
  const [planName, setPlanName] = useState('');
  const [reusePlanId, setReusePlanId] = useState('');

  useEffect(() => {
    if (!open) return;
    setQuery(''); setResults(null); setChosen(null); setPrice(''); setMonths('1'); setTestPlanId('');
    api.get('/customer-plans').then((r) => setPlans(r.data || [])).catch(() => {});
    (async () => {
      try {
        setLoadingSources(true);
        const r = await api.get('/imports/sources');
        const list = r.data.sources || [];
        setSources(list);
        if (list.length) setSourceKey((prev) => prev && list.some((s) => `${s.type}:${s.id}` === prev) ? prev : `${list[0].type}:${list[0].id}`);
      } catch {
        toast.error('Erro ao carregar painéis conectados');
      } finally {
        setLoadingSources(false);
      }
    })();
  }, [open, api]);

  const source = sources.find((s) => `${s.type}:${s.id}` === sourceKey);
  // planos já criados que pertencem a este painel (para reutilizar na importação)
  const plansForSource = () => {
    if (!source) return [];
    // SÓ os planos vinculados a ESTA integração
    const exatos = plans.filter((p) => p.active !== false && p.sigma_login_integration_id === source.id);
    if (exatos.length > 0) return exatos;
    // planos token ainda não migrados (sem integração) só para fonte Sigma
    if (source.provider === 'sigma') {
      return plans.filter((p) => p.active !== false && p.sigma_package_id && !p.sigma_login_integration_id);
    }
    return exatos;
  };

  const search = async () => {
    if (!source || !query.trim()) { toast.error('Digite o usuário do cliente no painel'); return; }
    try {
      setSearching(true); setResults(null); setChosen(null);
      const r = await api.post(`/imports/${source.type}/${source.id}/search`, { query: query.trim() });
      setResults(r.data.customers || []);
      if (!(r.data.customers || []).length) toast.info('Nenhum cliente encontrado com esse usuário');
    } catch (e) {
      toast.error(errText(e, 'Erro ao buscar no painel'));
    } finally {
      setSearching(false);
    }
  };

  const confirm = async (client) => {
    const c = client || chosen;
    if (!c || !source) return;
    let plan = null;
    if (c.is_test) {
      if (testPlanId && testPlanId !== 'new') {
        plan = plansForSource().find((p) => p.id === testPlanId) || plans.find((p) => p.id === testPlanId) || null;
        if (!plan) { toast.error('Escolha o plano que este cliente de teste terá'); return; }
      }
      // se testPlanId === 'new' ou vazio, cai no fluxo de criar plano novo abaixo (plan = null)
    } else if (reusePlanId && reusePlanId !== 'new') {
      // prioriza o plano que o usuário SELECIONOU no dropdown
      plan = plansForSource().find((p) => p.id === reusePlanId) || plans.find((p) => p.id === reusePlanId) || null;
    } else if (c.matched_plan) {
      // senão, usa o plano casado automaticamente (só se não estiver criando um novo)
      plan = c.matched_plan;
    }
    try {
      setResolving(true);
      if (!plan) {
        if (price === '' || Number(price) < 0) { toast.error('Informe o preço do plano'); setResolving(false); return; }
        const r = await api.post(`/imports/${source.type}/${source.id}/plan`, {
          package_id: c.package_id, package_name: (planName.trim() || c.package_name || 'Plano'), connections: c.connections || 1,
          panel_customer_id: c.panel_customer_id,
          price: parseFloat(price), duration_months: parseInt(months, 10) || 1,
        });
        plan = r.data.plan;
      }
      onImported({
        name: c.name || c.username || '',
        phone: (c.whatsapp || '').replace(/\D/g, ''),
        plan,
        expiration_date: (c.expiration_date || '').split('T')[0],
        import_data: {
          source: source.type,
          integration_id: source.id,
          panel: source.panel,
          panel_customer_id: c.panel_customer_id,
          username: c.username,
          password: c.password,
          package_id: c.package_id,
          connections: c.connections || 1,
          is_trial: !!c.is_test,
          panel_phone: c.whatsapp || '',
        },
      });
      onOpenChange(false);
    } catch (e) {
      toast.error(errText(e, 'Erro ao preparar importação'));
    } finally {
      setResolving(false);
    }
  };

  const pick = (c) => {
    if (c.already_imported) return;
    if (c.matched_plan && !c.is_test) confirm(c);
    else {
      setPlanName(c.package_name || c.username || '');
      const reusable = plansForSource();
      setReusePlanId(reusable.length ? reusable[0].id : 'new');
      setChosen(c);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Importar cliente</DialogTitle>
          <DialogDescription>Busque o cliente no painel para preencher plano e vencimento automaticamente.</DialogDescription>
        </DialogHeader>

        {loadingSources ? (
          <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-muted-foreground" /></div>
        ) : sources.length === 0 ? (
          <div className="py-6 text-center space-y-3">
            <p className="text-sm text-muted-foreground">Você ainda não conectou nenhum painel.</p>
            <Button variant="outline" onClick={() => { onOpenChange(false); window.location.href = '/integrations'; }}>
              <Link2 className="w-4 h-4 mr-2" /> Conectar em Integrações
            </Button>
          </div>
        ) : chosen ? (
          <div className="space-y-4">
            <button type="button" className="text-xs text-muted-foreground flex items-center gap-1 hover:text-foreground" onClick={() => setChosen(null)}>
              <ArrowLeft className="w-3 h-3" /> Voltar aos resultados
            </button>
            <div className="p-3 rounded-lg bg-muted/40 text-sm">
              <p className="font-medium">{chosen.username}</p>
              <p className="text-xs text-muted-foreground">{chosen.package_name} · {chosen.connections || 1} tela(s) · vence {chosen.expiration_date || '—'}</p>
            </div>
            {chosen.is_test ? (
              <div className="space-y-2">
                <p className="text-sm"><span className="text-amber-500 font-medium">Pacote de teste.</span> Escolha o plano que este cliente terá no NexuZap:</p>
                <Select value={testPlanId} onValueChange={setTestPlanId}>
                  <SelectTrigger><SelectValue placeholder="Selecione o plano..." /></SelectTrigger>
                  <SelectContent>
                    {(plansForSource().length > 0 ? plansForSource() : []).map((p) => (
                      <SelectItem key={p.id} value={p.id}>{p.name} - R$ {Number(p.price || 0).toFixed(2)}</SelectItem>
                    ))}
                    <SelectItem value="new">+ Criar novo plano</SelectItem>
                  </SelectContent>
                </Select>
                {plansForSource().length === 0 && <p className="text-xs text-amber-500">Nenhum plano desta integração ainda. Escolha "Criar novo plano".</p>}
                {(testPlanId === 'new' || plansForSource().length === 0) && (
                  <div className="space-y-2 pt-2 border-t border-border/40">
                    <p className="text-xs text-muted-foreground">Novo plano deste painel:</p>
                    <Input value={planName} onChange={(e) => setPlanName(e.target.value)} placeholder="Nome (ex.: Mensal, Teste)" />
                    <div className="grid grid-cols-2 gap-3">
                      <Input type="number" min="0" step="0.01" value={price} onChange={(e) => setPrice(e.target.value)} placeholder="Preço" />
                      <Input type="number" min="1" step="1" value={months} onChange={(e) => setMonths(e.target.value)} placeholder="Meses" />
                    </div>
                  </div>
                )}
              </div>
            ) : (<>
            {plansForSource().length > 0 && (
              <div className="space-y-1">
                <Label className="text-xs">Plano</Label>
                <Select value={reusePlanId} onValueChange={setReusePlanId}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {plansForSource().map((p) => (
                      <SelectItem key={p.id} value={p.id}>{p.name} — R$ {Number(p.price || 0).toFixed(2)} ({p.duration_months || 1} {(p.duration_months || 1) === 1 ? 'mês' : 'meses'})</SelectItem>
                    ))}
                    <SelectItem value="new">+ Criar novo plano</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
            {(plansForSource().length === 0 || reusePlanId === 'new') && (<>
              <p className="text-xs text-muted-foreground">Novo plano deste painel (fica disponível para reutilizar depois):</p>
              <div className="space-y-1">
                <Label className="text-xs">Nome do plano</Label>
                <Input value={planName} onChange={(e) => setPlanName(e.target.value)} placeholder="Ex.: Mensal, Anual, Combo 2 telas" autoFocus />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label className="text-xs">Preço (R$)</Label>
                  <Input type="number" min="0" step="0.01" value={price} onChange={(e) => setPrice(e.target.value)} />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Duração (meses)</Label>
                  <Input type="number" min="1" step="1" value={months} onChange={(e) => setMonths(e.target.value)} />
                </div>
              </div>
            </>)}
            </>)}
            <Button className="w-full" onClick={() => confirm()} disabled={resolving}>
              {resolving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
              {chosen.is_test ? 'Usar este cliente' : (reusePlanId && reusePlanId !== 'new' && plansForSource().length > 0 ? 'Usar este cliente' : 'Criar plano e usar este cliente')}
            </Button>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="space-y-1">
              <Label className="text-xs">Importar de</Label>
              <Select value={sourceKey} onValueChange={(v) => { setSourceKey(v); setResults(null); }}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {sources.map((s) => <SelectItem key={`${s.type}:${s.id}`} value={`${s.type}:${s.id}`}>{s.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="flex gap-2">
              <Input placeholder="Usuário do cliente no painel" value={query}
                onChange={(e) => setQuery(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && search()} />
              <Button onClick={search} disabled={searching}>
                {searching ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
              </Button>
            </div>
            {searching && <p className="text-xs text-muted-foreground">Consultando o painel… pode levar até 1 minuto.</p>}

            {results && results.length > 0 && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5 max-h-80 overflow-y-auto pr-1">
                {results.map((c) => (
                  <div key={c.panel_customer_id} className="flex flex-col gap-2 p-3 rounded-xl border border-border/60 bg-muted/20">
                    <div className="min-w-0">
                      <p className="font-semibold truncate">{c.username}</p>
                      <p className="text-[11px] text-muted-foreground mt-0.5">{c.package_name || 'Plano'}</p>
                      <p className="text-[11px] text-muted-foreground">{c.connections || 1} tela(s) · vence {c.expiration_date || '—'}</p>
                      {c.is_test
                        ? <p className="text-[11px] text-amber-500 mt-1">Teste — vai pedir o plano</p>
                        : c.matched_plan
                          ? <p className="text-[11px] text-emerald-500 mt-1 truncate">Plano: {c.matched_plan.name}</p>
                          : <p className="text-[11px] text-amber-500 mt-1">Sem plano — vai pedir o preço</p>}
                    </div>
                    {c.already_imported ? (
                      <Badge variant="outline" className="w-full justify-center py-1">Já cadastrado</Badge>
                    ) : (
                      <Button size="sm" className="w-full" onClick={() => pick(c)} disabled={resolving}>Usar este</Button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
